import { format, subDays } from 'date-fns';
import React from 'react';
import { Link } from 'react-router-dom';

import { AdherenceSummary } from '../../../../../components/Adherence/types';
import CircularProgress from '@hero/ui-kit/components/CircularProgress';
import Loader from '@hero/ui-kit/graphics/Loader';
import Section from '@hero/ui-kit/layout/Section';
import getMemberUrl from '../../../utils/getMemberUrl';
import useGetAdherenceSummary from '../../AdherenceModule/api/useGetAdherenceSummary';
import useGetMemberClinicalData from '../../EhrRecord/api/useGetMemberClinicalData';
import { hasEhrRecord } from '../../EhrRecord/utils/clinicalSummary';
import { useOrganizationMemberDetailsContext } from '../../context/OrganizationMemberDetailsContext';
import * as Style from './style.module.scss';

type Stat = { label: string; value: number };

/** Hero medication count, plus the EHR count when the member has an EHR record. */
const useMedicationStats = (): Stat[] => {
    const { memberDetails } = useOrganizationMemberDetailsContext();
    const { data: clinicalData } = useGetMemberClinicalData(memberDetails?.member_info?.member_id);

    const stats = [{ label: 'Hero meds', value: memberDetails?.medication_info?.length ?? 0 }];

    if (hasEhrRecord(clinicalData)) {
        stats.push({
            label: 'EHR meds',
            value: clinicalData.medications.filter((medication) => !medication.removed_at).length
        });
    }

    return stats;
};

const StatRow: React.FC<{ stats: Stat[] }> = ({ stats }) => (
    <div className={Style.stats}>
        {stats.map(({ label, value }) => (
            <div key={label} className={Style.stat}>
                <h5>{value}</h5>
                <small>{label}</small>
            </div>
        ))}
    </div>
);

const getProgressColor = (percent: number) => {
    if (percent > 80) {
        return 'green';
    }
    return percent < 50 ? 'red' : 'orange';
};

const AdherenceSummaryCircle: React.FC = () => {
    const { memberDetails } = useOrganizationMemberDetailsContext();
    const heroUserId = memberDetails?.member_info?.hero_user_id || 0;
    const memberId = memberDetails?.member_info?.member_id;
    const adherenceUrl = getMemberUrl(heroUserId, memberId, 'adherence');

    const fetchAdherenceSummary = useGetAdherenceSummary(heroUserId);
    const medicationStats = useMedicationStats();

    const [state, setState] = React.useState<{
        isLoading: boolean;
        data: AdherenceSummary | undefined;
    }>({
        isLoading: false,
        data: undefined
    });

    React.useEffect(() => {
        if (heroUserId) {
            setState({ isLoading: true, data: undefined });
            const today = format(new Date(), 'yyyy-MM-dd');
            fetchAdherenceSummary({ mode: 'weekly', target_datetime: `${today} 00:00:00` })
                .then((response) => setState({ isLoading: false, data: response }))
                .catch(() => setState({ isLoading: false, data: undefined }));
        }
    }, [heroUserId]);

    if (state.isLoading) {
        return (
            <Section centered styles={{ height: '100%' }}>
                <Loader />
            </Section>
        );
    }

    if (!state.data) {
        return (
            <div className={Style.wrapper}>
                <Section centered>
                    <p>No adherence data</p>
                </Section>
                <StatRow stats={medicationStats} />
            </div>
        );
    }

    const { data } = state;
    const pillsTaken = data.positive;
    const pillsMissed = data.negative + data.unknown;
    const dosesTaken = data.positive_doses;
    const dosesMissed = data.negative_doses + data.unknown_doses;
    const percentTaken = Math.round((pillsTaken * 100) / (pillsMissed + pillsTaken)) || 0;

    const today = new Date();
    const weekAgo = subDays(today, 6);
    const dateRange = `${format(weekAgo, 'MMM d')} - ${format(today, 'MMM d, yyyy')}`;

    return (
        <div className={Style.wrapper}>
            <Section centered noDefaultPadding marginBottom="regular">
                <Link to={adherenceUrl} className={Style.titleLink}>
                    <h6>Adherence</h6>
                    <small>{dateRange}</small>
                </Link>
            </Section>
            <Section centered noDefaultPadding marginBottom="regular" className={Style.progress}>
                <CircularProgress
                    size={170}
                    progress={[{ percent: percentTaken, color: getProgressColor(percentTaken) }]}
                    border
                />
                <div>
                    <h2>
                        {percentTaken}
                        <sup>%</sup>
                    </h2>
                    <small>
                        {pillsTaken} of {pillsTaken + pillsMissed} reported
                    </small>
                </div>
            </Section>

            <StatRow
                stats={[
                    { label: 'Dose taken', value: dosesTaken },
                    { label: 'Dose missed', value: dosesMissed },
                    ...medicationStats
                ]}
            />
        </div>
    );
};

export default AdherenceSummaryCircle;
